import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Grid } from '@mui/material';
import { useFormikContext } from 'formik';

import { FormikAutocomplete } from '@/components/formik/FormikAutocomplete';
import { FormikText } from '@/components/formik/FormikText';
import { PlacesAutosuggest } from '@/components/placesAutosuggest/PlacesAutosuggest';
import type { TSearchResult } from '@/components/placesAutosuggest/types';

import type { TAddress } from '@/types/common';

import { STATES, STATE_ABBREVIATIONS } from '@/mocks/states';

type TFormDataExt = {
  location: TAddress & { formatted: string };
};

export const FormikLocation = () => {
  const [manual, setManual] = useState(false);

  const { t } = useTranslation();
  const { values, errors, setFieldValue, submitCount } = useFormikContext<TFormDataExt>();
  useEffect(() => {
    const errorsCount = Object.keys(errors.location || {}).length;

    // Submit attempted, location doesn't have all required fields but not empty - switch to manual
    if (submitCount > 0 && errorsCount > 0 && errorsCount < 7) {
      setManual(true);
    }
  }, [submitCount, errors.location]);

  useEffect(() => {
    setFieldValue('location.stateFullName', STATES[values.location.state]);
  }, [values.location.state, setFieldValue]);

  if (manual) {
    return (
      <>
        <FormikText name="location.addressLine1" label="formLabels.streetAddress" />
        <FormikText name="location.addressLine2" label="formLabels.secondaryAddress" />

        <Grid spacing={2} container>
          <Grid xs={12} laptop={6} item>
            <FormikText name="location.city" label="formLabels.city" />
          </Grid>

          <Grid xs={12} laptop={6} item>
            <FormikAutocomplete
              options={STATE_ABBREVIATIONS}
              name="location.state"
              label="formLabels.state"
            />
          </Grid>
        </Grid>

        <FormikText name="location.zipCode" label="formLabels.zip" />
      </>
    );
  }

  const onPlaceChange = (
    { address, zip, ...rest }: Partial<TSearchResult>,
    place?: google.maps.places.PlaceResult,
  ) => {
    if (place) {
      setFieldValue('location', {
        bounds: place.geometry?.viewport,
        addressLine1: address,
        addressLine2: '',
        zipCode: zip,
        ...rest,
      });
    }
  };

  const switchToManual = () => {
    setFieldValue('location.country', 'USA');
    setManual(true);
  };

  return (
    <>
      <PlacesAutosuggest
        name="location"
        label="stationSetup.generalInfo.searchByStreet"
        initialInputValue={values.location.formatted}
        setSearchResult={onPlaceChange}
        error={
          submitCount > 0 && errors.location
            ? 'stationSetup.generalInfo.selectFromAutocomplete'
            : undefined
        }
      />

      <Box
        sx={{
          marginTop: 1.5,
          marginBottom: 3.5,
          textAlign: 'center',
        }}
      >
        <Button variant="text" size="large" onClick={switchToManual} disableRipple>
          {t('stationSetup.generalInfo.enterAddressManually')}
        </Button>
      </Box>
    </>
  );
};
